import React from 'react';
import {emotionStyled} from '@packages/utilities/styled';
import OneLiner from '@packages/systems/design-system/OneLiner';
import {useTranslation} from '@packages/systems/designer-translations';

import {
  AddIcon,
  SettingsIcon,
  ConditionalRuleIcon,
  UnlinkIcon,
} from '@packages/systems/spring';

export type ExpressionBindingButtonProps = {
  ['data-automation-id']: string;
  onClick: () => void;
  bindingLabel?: string;
};

export const ExpressionBindingButton = emotionStyled('button')({
  appearance: 'none',
  background: 'transparent',
  width: '100%',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  gap: '$2',
  color: '$textPrimary',
  padding: '$1 $2',
  fontSize: '$body',
  fontWeight: '$normal',
  textDecoration: 'none',
  border: 'none',
  '&:hover, &:focus-visible': {
    background: '$uiBackgroundHover',
    color: '$textPrimary',
    outline: 'none',
  },
});

const IconContainer = emotionStyled('div')({
  width: '$4',
  height: '$4',
});

const NewFieldStyledButton = emotionStyled(ExpressionBindingButton)({
  '&&, &&:hover, &&:focus-visible': {
    color: '$purpleText',
  },
});

export const EditOverrideButton = (props: ExpressionBindingButtonProps) => {
  const t = useTranslation();
  return (
    <ExpressionBindingButton {...props}>
      <IconContainer>
        <SettingsIcon colorVariant="secondary" />
      </IconContainer>
      {t('designer.components.settings.properties.edit-settings')}
    </ExpressionBindingButton>
  );
};

export const NewFieldButton = (props: ExpressionBindingButtonProps) => {
  const t = useTranslation();

  return (
    <NewFieldStyledButton {...props}>
      <IconContainer>
        <AddIcon />
      </IconContainer>
      <OneLiner>
        {t('designer.components.settings.properties.new', {
          // default to "New property" if bindingLabel is missing
          propertyType: props.bindingLabel || '',
        })}
      </OneLiner>
    </NewFieldStyledButton>
  );
};

export const UnlinkButton = (props: ExpressionBindingButtonProps) => {
  const t = useTranslation();
  return (
    <ExpressionBindingButton {...props}>
      <IconContainer>
        <UnlinkIcon colorVariant="secondary" />
      </IconContainer>
      {t('designer.components.settings.properties.disconnect')}
    </ExpressionBindingButton>
  );
};

export const NewConditionalButton = (props: ExpressionBindingButtonProps) => {
  const t = useTranslation();
  return (
    <ExpressionBindingButton {...props}>
      <IconContainer>
        <ConditionalRuleIcon colorVariant="secondary" />
      </IconContainer>
      {t('designer.components.settings.properties.new-conditional')}
    </ExpressionBindingButton>
  );
};
